import { hasSetupDetails } from './workspace/setupPresence.js'

const IDEA_LIKE_STAGES = new Set(['idea', 'planned', ''])
const ALWAYS_VISIBLE_STAGES = new Set(['active', 'closed', 'reviewed'])

function hasTrackedSchwabOrders(project) {
  return Array.isArray(project?.schwab_order_ids) && project.schwab_order_ids.length > 0
}

// Tracked entry parents are durable submission evidence, not proof of a fresh
// live snapshot. Until explicitly retired, they must never reopen an unsent
// Idea workflow merely because a sync stored the wrong lifecycle labels.
export function hasSubmittedSetupOrders(project) {
  const stage = String(project?.stage || 'idea').trim().toLowerCase()
  const status = String(project?.setup_status || '').trim().toLowerCase()
  if (!['idea', 'planned', 'setup', 'working'].includes(stage)) return false
  if (!['', 'idea', 'planned', 'working', 'failed'].includes(status)) return false
  return (Array.isArray(project?.schwab_order_ids) ? project.schwab_order_ids : []).some(row => {
    const role = String(row?.role || '').trim().toUpperCase()
    const side = String(row?.side || '').trim().toUpperCase()
    return (!role || ['ENTRY', 'TARGET_ENTRY', 'RUNNER_ENTRY', 'ORPHAN_NEEDS_CLEANUP'].includes(role))
      && (!side || ['BUY', 'BUY_TO_OPEN'].includes(side))
      && String(row?.accountNumber ?? row?.account_number ?? '').trim()
      && String(row?.orderId ?? row?.order_id ?? '').trim()
  })
}

export function projectSetupLifecycle(project) {
  if (!hasSubmittedSetupOrders(project)) return project
  if (project.stage === 'working' && project.setup_status === 'working') return project
  return { ...project, stage: 'working', setup_status: 'working' }
}

export function resolvePositionListStage(project, {
  hasLivePosition = false,
  hasLinkedTrade = false,
  hasOrderEvidence = hasTrackedSchwabOrders(project),
} = {}) {
  const storedStage = String(project?.stage || 'idea').trim().toLowerCase()
  const setupStatus = String(project?.setup_status || '').trim().toLowerCase()
  const hasBrokerEvidence = Boolean(hasLivePosition || hasLinkedTrade || hasOrderEvidence)

  // Asset grouping follows current holdings even while setup metadata still
  // says Working (including partial fills). Closed campaigns remain history.
  if (hasLivePosition && (IDEA_LIKE_STAGES.has(storedStage) || storedStage === 'working')) {
    return 'active'
  }

  if (hasSubmittedSetupOrders(project)) return 'working'

  // Older partial setup sends were persisted as failed/idea even when some
  // entry orders reached Schwab. Keep those legacy rows in Working until the
  // tracked broker refs are explicitly reconciled or cleared. A transiently
  // empty scoped snapshot is not strong enough evidence to hide a setup that
  // may still have live orders.
  if (setupStatus === 'failed' && IDEA_LIKE_STAGES.has(storedStage) && hasTrackedSchwabOrders(project)) {
    return 'working'
  }

  if (setupStatus === 'working' && IDEA_LIKE_STAGES.has(storedStage)) {
    return hasBrokerEvidence ? 'working' : 'idea'
  }

  if (storedStage === 'working' && !hasBrokerEvidence) {
    return 'idea'
  }

  if (storedStage === 'planned' || storedStage === '') {
    return 'idea'
  }

  return storedStage
}

export function shouldShowPositionListProject(project, {
  hasLivePosition = false,
  hasLinkedTrade = false,
  hasOrderEvidence = undefined,
} = {}) {
  const stage = resolvePositionListStage(project, { hasLivePosition, hasLinkedTrade, hasOrderEvidence })
  if (ALWAYS_VISIBLE_STAGES.has(stage)) return true
  if (hasLivePosition || hasLinkedTrade) return true
  if (stage === 'idea' && project?.setup_source === 'Ideas List' && project?.setup_status === 'idea') return true
  if (!hasSetupDetails(project)) return false
  return true
}
